import { useEffect, useRef, useState } from "react";
import { Card, Form, Offcanvas } from "react-bootstrap";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import { set, useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { toast } from "react-toastify";
import WebService from "../../utility/WebService";
import CustomButton from "../../components/CustomButton/CustomButton";
import CommonQuill from "../../components/editor/CommonQuill";
import HelperService from "../../utility/HelperService";

interface AddStartupAndInnovationProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  title: string;
  slug: string;
  createdBy: string;
  meta_description: string;
  keyword: string;
};

const AddStartupAndInnovation = ({
  show,
  handleClose,
  data,
}: AddStartupAndInnovationProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger,
  } = useForm<FormData>();
  const [isLoading, setLoading] = useState(false);
  const [logo, setLogo] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [fileError, setFileError] = useState<string>("");
  const [images, setImages] = useState<any[]>([{}]);
  const descriptionQuillRef = useRef<any>(null);
  const shortDescQuillRef = useRef<any>(null);
  const [customErrors, setCustomErrors] = useState<{
    description?: string;
    short_description?: string;
  }>({});
  const [ecoSystemData, setEcoSystemData] = useState<any[]>([
    { title: "", description: "", image: null },
  ]);
  const [requirementsData, setRequirementsData] = useState<any[]>([
    { title: "" },
  ]);
  const [keyEcoSystemEnablers, setKeyEcoSystemEnablers] = useState<any[]>([
    { title: "" },
  ]);

  const [fundRaisingSupport, setFundRaisingSupport] = useState<any[]>([
    { title: "" },
  ]);

  const [members, setMembers] = useState<any[]>([
    { name: "", logo: "", type: "MEMBER" },
  ]);

  useEffect(() => {
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    if (data) {
      reset({
        title: data.title || "",
        slug: data.slug,
        keyword: data?.keyword,
        createdBy: userData?.fullName || "",
        meta_description: data?.meta_description,
      });
      setDescription(data.description || "");
      setShortDescription(data.short_description || "");
      setImages(data.images && data.images.length > 0 ? data.images : [{}]);
      setEcoSystemData(
        data.ecoSystem && data.ecoSystem.length > 0
          ? data.ecoSystem
          : [{ title: "", description: "", image: null }],
      );
      setRequirementsData(
        data.requirements && data.requirements.length > 0
          ? data.requirements
          : [{ title: "" }],
      );
      setKeyEcoSystemEnablers(
        data?.keyEcoSystemEnablers?.length
          ? data.keyEcoSystemEnablers
          : [{ title: "" }],
      );

      setFundRaisingSupport(
        data?.fundRaisingSupport?.length
          ? data.fundRaisingSupport
          : [{ title: "" }],
      );

      setMembers(
        data?.members?.length
          ? data.members
          : [{ name: "", logo: "", type: "MEMBER" }],
      );
    }
  }, [data, show]);
  const isQuillEmpty = (value?: string) => {
    if (!value) return true;

    // remove html tags, &nbsp; and spaces
    const text = value
      .replace(/<(.|\n)*?>/g, "")
      .replace(/&nbsp;/g, "")
      .trim();

    return text.length === 0;
  };
  const validateInfoTab = async () => {
    const titleValid = await trigger(["title"]);

    const newErrors: any = {};

    if (isQuillEmpty(description)) {
      newErrors.description = "Description is required";
    }

    if (isQuillEmpty(shortDescription)) {
      newErrors.short_description = "Short Description is required";
    }

    setCustomErrors(newErrors);

    // 🔥 IMPORTANT: return actual validity
    return titleValid && !newErrors.description;
  };
  const uploadFile = (
    file: File,
    index: number,
    type: string,
    subIndex?: number,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storage", "STARTUPINNOVATION");
    WebService.fileUploadAPI({
      action: `upload`,
      body: formData,
    })
      .then((res: any) => {
        if (res.data) {
          if (type === "GALLERY") {
            images[index] = res.data?.url;
            setImages([...images]);
          } else if (type === "ECO_SYSTEM") {
            ecoSystemData[index].image = res.data?.url;
            setEcoSystemData([...ecoSystemData]);
          } else if (type === "MEMBER") {
            members[index].logo = res.data?.url;
            setMembers([...members]);
          }
        }
      })
      .catch((err: any) => {
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  const onSubmit = async (request: FormData) => {
    if (fileError) {
      toast.error(fileError);
      return;
    }
    const valid = await validateInfoTab();
    if (!valid) {
      toast.error("Please fill required fields");
      return;
    }
    setLoading(true);
    const formData = new FormData();
    const storedUser = localStorage.getItem("UserData");
    const userData = storedUser ? JSON.parse(storedUser) : null;
    formData.append("title", request.title);

    const editor = descriptionQuillRef.current?.getEditor();
    const html = editor?.root.innerHTML || "";
    formData.append("description", html);
    formData.append("short_description", shortDescription);

    formData.append("meta_description", request.meta_description);
    formData.append("keyword", request.keyword);
    if (data?._id) {
      formData.append("id", data._id);
    }
    if (!data?._id && userData?._id) {
      formData.append("createdBy", userData._id);
    }
    if (
      images.length > 0 &&
      images.every((img) => img && Object.keys(img).length !== 0 && img !== "")
    ) {
      formData.append("images", JSON.stringify(images));
    }
    if (
      ecoSystemData.length > 0 &&
      ecoSystemData.every((eco) => eco.title || eco.description || eco.image)
    ) {
      formData.append("ecoSystem", JSON.stringify(ecoSystemData));
    }
    if (
      requirementsData.length > 0 &&
      requirementsData.every((req) => req.title)
    ) {
      formData.append("requirements", JSON.stringify(requirementsData));
    }
    if (keyEcoSystemEnablers.length) {
      formData.append(
        "keyEcoSystemEnablers",
        JSON.stringify(keyEcoSystemEnablers),
      );
    }

    if (fundRaisingSupport.length) {
      formData.append("fundRaisingSupport", JSON.stringify(fundRaisingSupport));
    }

    if (members.length) {
      formData.append("members", JSON.stringify(members));
    }
    if (logo) {
      formData.append("logo", logo);
    }
    WebService.fileUploadAPI({
      action: `startup-and-innovation/addContent`,
      body: formData,
    })
      .then((res: any) => {
        toast.success(res.message);
        setLoading(false);
        handleClose();
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(err.response?.data?.message || "An error occurred");
      });
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{data ? "Edit" : "Add"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Main Content
              </span>

              <Form.Group className="mb-2" controlId="title">
                <Form.Label className="label">Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter title"
                  {...register("title", { required: "Title is required" })}
                />
                {errors.title && (
                  <div className="error-message">{errors.title.message}</div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="slugField">
                <Form.Label className="label">Slug</Form.Label>{" "}
                <span className="text-danger">*</span>
                <Form.Control
                  type="text"
                  placeholder="Auto-generated slug"
                  {...register("slug")}
                  value={watch("slug") || ""}
                  // disabled // ✅ keep it readonly
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId="eventDescription">
                <Form.Label className="label">Description</Form.Label>

                <CommonQuill
                  ref={descriptionQuillRef}
                  value={description}
                  onChange={setDescription}
                  storage="STARTUPINNOVATION"
                  initialHtml={show && data?.description ? HelperService.normalizeResizeHtml(data.description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      description: undefined,
                    }))
                  }
                />
                {customErrors.description && (
                  <div className="error-message">
                    {customErrors.description}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-2" controlId="eventDescription">
                <Form.Label className="label">Short Description</Form.Label>

                <CommonQuill
                  ref={shortDescQuillRef}
                  value={shortDescription}
                  onChange={setShortDescription}
                  storage="STARTUPINNOVATION"
                  initialHtml={show && data?.short_description ? HelperService.normalizeResizeHtml(data.short_description) : undefined}
                  onClearError={() =>
                    setCustomErrors((p) => ({
                      ...p,
                      short_description: undefined,
                    }))
                  }
                />
                {customErrors.short_description && (
                  <div className="error-message">
                    {customErrors.short_description}
                  </div>
                )}
              </Form.Group>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Part of Ecosystem
                </span>
                <Form.Group className="mb-2" controlId="Part of Ecosystem">
                  {ecoSystemData.map((ecoSystem, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "EcoSystemIndex",
                          index.toString(),
                        );
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("EcoSystemIndex"),
                        );
                        if (fromIndex === index) return;
                        const newEcoSystemData = [...ecoSystemData];
                        const [moved] = newEcoSystemData.splice(fromIndex, 1);
                        newEcoSystemData.splice(index, 0, moved);
                        setEcoSystemData(newEcoSystemData);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>
                        <div style={{ flex: 1 }} className="highlight-item">
                          <Form.Control
                            type="file"
                            accept="image/*"
                            className="custom-image-upload"
                            onChange={(e) => {
                              const input = e.target as HTMLInputElement;
                              const file = input.files?.[0];

                              if (!file) return;

                              if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                                const msg =
                                  "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                                setFileError(msg);
                                toast.error(msg);
                                input.value = "";
                                return;
                              }
                              setFileError("");
                              uploadFile(file, index, "ECO_SYSTEM");
                            }}
                          />

                          <p
                            className="text-primary mt-1"
                            style={{ fontSize: "13px" }}
                          >
                            Note: Please upload an image of size:{" "}
                            <strong>44 × 44 px</strong> in{" "}
                            <strong>.png, .jpeg, .jpg or .svg</strong> format.
                          </p>
                          {ecoSystemData[index] && (
                            <img
                              src={
                                ecoSystemData[index].image
                                  ? ecoSystemData[index].image
                                  : data?.ecoSystemData?.[index]?.imageUrl || ""
                              }
                              style={{
                                maxWidth: "100%",
                                maxHeight: 100,
                                objectFit: "contain",
                                borderRadius: 8,
                                marginTop: 10,
                                marginBottom: 10,
                              }}
                            />
                          )}
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder={`Name`}
                              value={ecoSystemData[index].title}
                              onChange={(e) => {
                                const newEcoSystemData = [...ecoSystemData];
                                newEcoSystemData[index].title = e.target.value;
                                setEcoSystemData(newEcoSystemData);
                              }}
                            />
                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() => {
                                  setEcoSystemData([
                                    ...ecoSystemData,
                                    { title: "", description: "", image: null },
                                  ]);
                                }}
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {ecoSystemData.length > 1 && (
                                <DashSquareFill
                                  onClick={() => {
                                    const newEcoSystemData =
                                      ecoSystemData.filter(
                                        (_, i) => i !== index,
                                      );
                                    setEcoSystemData(newEcoSystemData);
                                  }}
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                          <Form.Control
                            type="text"
                            placeholder={`Description`}
                            value={ecoSystemData[index].description}
                            onChange={(e) => {
                              const newEcoSystemData = [...ecoSystemData];
                              newEcoSystemData[index].description =
                                e.target.value;
                              setEcoSystemData(newEcoSystemData);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Key Ecosystem Enablers
                </span>
                <Form.Group className="mb-2" controlId="KeyEcosystemEnablers">
                  {keyEcoSystemEnablers.map((item, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData(
                          "KeyEnablerIndex",
                          index.toString(),
                        )
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("KeyEnablerIndex"),
                        );
                        if (fromIndex === index) return;
                        const updated = [...keyEcoSystemEnablers];
                        const [moved] = updated.splice(fromIndex, 1);
                        updated.splice(index, 0, moved);
                        setKeyEcoSystemEnablers(updated);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        {/* drag icon */}
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>

                        <div style={{ flex: 1 }} className="highlight-item">
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder="Title"
                              value={item.title}
                              onChange={(e) => {
                                const updated = [...keyEcoSystemEnablers];
                                updated[index].title = e.target.value;
                                setKeyEcoSystemEnablers(updated);
                              }}
                            />

                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() =>
                                  setKeyEcoSystemEnablers([
                                    ...keyEcoSystemEnablers,
                                    { title: "" },
                                  ])
                                }
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {keyEcoSystemEnablers.length > 1 && (
                                <DashSquareFill
                                  onClick={() =>
                                    setKeyEcoSystemEnablers(
                                      keyEcoSystemEnablers.filter(
                                        (_, i) => i !== index,
                                      ),
                                    )
                                  }
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  Fund Raising Support
                </span>
                <Form.Group className="mb-2" controlId="FundRaisingSupport">
                  {fundRaisingSupport.map((item, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData("FundIndex", index.toString())
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("FundIndex"),
                        );
                        if (fromIndex === index) return;
                        const updated = [...fundRaisingSupport];
                        const [moved] = updated.splice(fromIndex, 1);
                        updated.splice(index, 0, moved);
                        setFundRaisingSupport(updated);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>

                        <div style={{ flex: 1 }} className="highlight-item">
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder="Title"
                              value={item.title}
                              onChange={(e) => {
                                const updated = [...fundRaisingSupport];
                                updated[index].title = e.target.value;
                                setFundRaisingSupport(updated);
                              }}
                            />
                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() =>
                                  setFundRaisingSupport([
                                    ...fundRaisingSupport,
                                    { title: "" },
                                  ])
                                }
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {fundRaisingSupport.length > 1 && (
                                <DashSquareFill
                                  onClick={() =>
                                    setFundRaisingSupport(
                                      fundRaisingSupport.filter(
                                        (_, i) => i !== index,
                                      ),
                                    )
                                  }
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Card className="p-3 bg-light my-3 position-relative">
                <span
                  className="bg-white position-absolute rounded small"
                  style={{ top: "-12px", left: "12px", padding: "0 8px" }}
                >
                  List Of Requirements From Start-Up
                </span>
                <Form.Group className="mb-2" controlId="Part of Ecosystem">
                  {requirementsData.map((requirement, index) => (
                    <div
                      key={index}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData(
                          "RequirementIndex",
                          index.toString(),
                        );
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        const fromIndex = Number(
                          e.dataTransfer.getData("RequirementIndex"),
                        );
                        if (fromIndex === index) return;
                        const newRequirementsData = [...requirementsData];
                        const [moved] = newRequirementsData.splice(
                          fromIndex,
                          1,
                        );
                        newRequirementsData.splice(index, 0, moved);
                        setRequirementsData(newRequirementsData);
                      }}
                      style={{ cursor: "move" }}
                    >
                      <div className="d-flex align-items-start">
                        <span
                          style={{
                            cursor: "grab",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            fontSize: 20,
                            color: "#888",
                            marginTop: 10,
                          }}
                          title="Drag to reorder"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <circle cx="5" cy="6" r="1.5" />
                            <circle cx="5" cy="10" r="1.5" />
                            <circle cx="5" cy="14" r="1.5" />
                            <circle cx="11" cy="6" r="1.5" />
                            <circle cx="11" cy="10" r="1.5" />
                            <circle cx="11" cy="14" r="1.5" />
                          </svg>
                        </span>
                        <div style={{ flex: 1 }} className="highlight-item">
                          <div className="d-flex">
                            <Form.Control
                              type="text"
                              placeholder={`Name`}
                              value={requirementsData[index].title}
                              onChange={(e) => {
                                const newRequirementsData = [
                                  ...requirementsData,
                                ];
                                newRequirementsData[index].title =
                                  e.target.value;
                                setRequirementsData(newRequirementsData);
                              }}
                            />
                            <div className="d-flex mt-2">
                              <PlusSquareFill
                                onClick={() => {
                                  setRequirementsData([
                                    ...requirementsData,
                                    { title: "" },
                                  ]);
                                }}
                                className="ms-2 me-2 cursor-pointer"
                              />
                              {requirementsData.length > 1 && (
                                <DashSquareFill
                                  onClick={() => {
                                    const newRequirementsData =
                                      requirementsData.filter(
                                        (_, i) => i !== index,
                                      );
                                    setRequirementsData(newRequirementsData);
                                  }}
                                  className="cursor-pointer"
                                />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Form.Group>
              </Card>
              <Form.Group className="mb-2" controlId="createdBy">
                <Form.Label className="label">Created By</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Created by user"
                  {...register("createdBy")}
                  value={watch("createdBy") || ""}
                  disabled
                />
              </Form.Group>
            </Card>
            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                Media
              </span>
              <Form.Group className="mb-2" controlId="logo">
                <Form.Label className="label">logo</Form.Label>
                <p
                  className="text-primary mt-1"
                  style={{ fontSize: "13px" }}
                >
                  Note: Please upload an image of size:{" "}
                  <strong>226 × 93 px</strong> in{" "}
                  <strong>.png, .jpeg, .jpg or .svg</strong> format.
                </p>
                <Form.Control
                  type="file"
                  accept="image/*"
                  className="custom-image-upload"
                  onChange={(e) => {
                    const input = e.target as HTMLInputElement;
                    const selectedFile = input.files?.[0];

                    if (!selectedFile) return;

                    if (!ALLOWED_IMAGE_TYPES.includes(selectedFile.type)) {
                      setFileError(
                        "Image format not supported. Please upload PNG, JPG, JPEG or SVG. image",
                      );
                      // console.log(fileError)
                      //      toast.error(fileError);
                      setLogo(null);
                      input.value = ""; // reset input
                      return;
                    }

                    // ✅ valid
                    setFileError("");
                    setLogo(selectedFile);
                  }}
                />
                {fileError && (
                  <p className="text-danger mt-1" style={{ fontSize: "13px" }}>
                    {fileError}
                  </p>
                )}
              </Form.Group>
              {(logo || data?.logo) && (
                <div className="mb-2 event-image">
                  <img
                    src={logo ? URL.createObjectURL(logo) : data?.logo}
                    alt="Event"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}

              <Form.Group className="mb-2" controlId="images">
                <Form.Label className="label">Images</Form.Label>
                {images.map((image, index) => (
                  <>
                    <div className="d-flex mt-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        className="custom-image-upload"
                        onChange={(e) => {
                          const input = e.target as HTMLInputElement;
                          const file = input.files && input.files[0];

                          if (file) {
                            const previewURL = URL.createObjectURL(file);

                            // 1️⃣ Local preview update
                            const updated = [...images];
                            updated[index] = {
                              ...updated[index],
                              preview: previewURL,
                            };
                            setImages(updated);

                            // 2️⃣ Upload to server
                            uploadFile(file, index, "GALLERY");
                          }
                        }}
                      />
                      <div className="d-flex mt-2">
                        <PlusSquareFill
                          onClick={() => {
                            setImages([...images, {}]);
                          }}
                          className="ms-2 me-2 cursor-pointer"
                        />
                        {images.length > 1 && (
                          <DashSquareFill
                            onClick={() => {
                              const newImages = images.filter(
                                (_, i) => i !== index,
                              );
                              setImages(newImages);
                            }}
                            className="cursor-pointer"
                          />
                        )}
                      </div>
                    </div>
                    {(image.preview || image.url) && (
                      <img
                        src={image.preview ? image.preview : image.url}
                        alt="Event"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 200,
                          objectFit: "contain",
                        }}
                      />
                    )}
                  </>
                ))}
              </Form.Group>
            </Card>

            <Card className="p-3 bg-light my-3 position-relative">
              <span
                className="bg-white position-absolute rounded small"
                style={{ top: "-12px", left: "12px", padding: "0 8px" }}
              >
                SEO
              </span>
              <Form.Label className="label">
                Meta Description
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                placeholder="Meta Description"
                {...register("meta_description", {
                  required: "Meta description is required",
                })}
              />
              {errors.meta_description && (
                <div className="error-message">
                  {errors.meta_description.message}
                </div>
              )}
              <Form.Group className="mb-2" controlId="eventName">
                <Form.Label className="label">
                  Keywords <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter keywords"
                  {...register("keyword", {
                    required: "Keywords are required",
                  })}
                />
                {errors.keyword && (
                  <div className="error-message">{errors.keyword.message}</div>
                )}
              </Form.Group>
            </Card>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddStartupAndInnovation;
